import { useData } from "../runtime/provider";
import { RouterInputs, RouterOutputs } from "@utils/trpc";
import { useCallback, useEffect } from "react";
import { AppState, AppStateStatus, Platform } from "react-native";
import * as Device from "expo-device";
import config from "@utils/config";
import { captureException } from "@utils/captureException";
import { auth } from "@utils/firebase";
import { NoAuthError } from "@utils/NoAuthError";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@utils/firebaseAuth/auth";

type TrackSessionInput = RouterInputs["user"]["trackSession"];
type TrackSessionOutput = RouterOutputs["user"]["trackSession"];

export default function useSessionTracking() {
  const session = useData();
  const { firebaseUser } = useAuth();
  const mutation = useMutation<TrackSessionOutput, Error, TrackSessionInput>({
    mutationFn: (input: TrackSessionInput) => {
      if (!auth.currentUser) {
        throw new NoAuthError("useSessionTracking mutationFn");
      }
      return session.api.user.trackSession.mutate(input);
    },
  });

  const trackSession = useCallback(async () => {
    console.log("useSessionTracking - tracking session...");

    // Provide fallbacks for web platform where Device APIs return undefined
    const getDeviceInfo = () => {
      if (Platform.OS === "web") {
        return {
          osVersion: navigator.userAgent || null,
          device: "Web Browser",
          deviceVersion: navigator.userAgent || null,
        };
      }

      return {
        osVersion: Device.osVersion || null,
        device: Device.modelName || null,
        deviceVersion: Device.osVersion || null,
      };
    };

    const deviceInfo = getDeviceInfo();

    await mutation.mutateAsync({
      appVersion: config.APP_VERSION || null,
      appBuildNumber: config.APP_BUILD_NUMBER || null,
      updateId: config.UPDATES.ID || null,
      channel: config.UPDATES.CHANNEL || null,
      os: Platform.OS || null,
      osVersion: deviceInfo.osVersion,
      device: deviceInfo.device,
      deviceVersion: deviceInfo.deviceVersion,
    });
  }, [mutation]);

  useEffect(() => {
    if (!firebaseUser) {
      return;
    }

    const subscription = AppState.addEventListener(
      "change",
      (status: AppStateStatus) => {
        if (status === "active") {
          console.log("::useSessionTracking:: app is active");
          trackSession().catch((error) => {
            captureException(error, {
              channel: "breadcrumb",
              area: "analytics",
              operation: "track-session-active",
            });
          });
        }

        if (status === "background") {
          console.log("::useSessionTracking:: app is going to the background");
        }

        if (status === "inactive") {
          console.log("::useSessionTracking:: app is inactive");
        }
      },
    );

    trackSession().catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "analytics",
        operation: "track-session-start",
      });
    });

    return () => subscription.remove();
  }, [firebaseUser]);
}
